import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { Alert, Select, Input } from '../ui';
export type Country = 'us-fda' | 'kr-mfds';
export interface KrProfile {
  productClass: 'health_functional_food' | 'ordinary_food' | 'unknown';
  context: 'label' | 'advertising';
  servingsPerDay: number;
}
export function CountrySelector({
  value,
  onChange,
  profile,
  onProfile,
  disabled,
  onReady,
}: {
  value: Country[];
  onChange: (v: Country[]) => void;
  profile: KrProfile;
  onProfile: (p: KrProfile) => void;
  disabled: boolean;
  onReady: (ready: boolean) => void;
}) {
  const { t } = useTranslation();
  const [countries, set] = useState<{ id: Country; label: string; ready: boolean; reason: string | null }[]>(
    [],
  );
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    void api<{ jurisdictions: typeof countries }>('/api/v1/compliance/jurisdictions')
      .then((r) => {
        if (!cancelled) set(r.jurisdictions);
      })
      .catch((e) => {
        if (!cancelled) setError(String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const kr = countries.find((x) => x.id === 'kr-mfds');
  useEffect(() => {
    onReady(
      value.length > 0 &&
        (!value.includes('kr-mfds') || (!!kr?.ready && profile.productClass === 'health_functional_food')),
    );
  }, [value, kr?.ready, profile.productClass, onReady]);
  return (
    <fieldset disabled={disabled} className="space-y-2 rounded border p-3 text-sm">
      <legend>{t('markets.choose')}</legend>
      <div className="flex flex-wrap gap-4">
        {(['us-fda', 'kr-mfds'] as Country[]).map((id) => (
          <label key={id} className="flex gap-2 items-center">
            <input
              type="checkbox"
              checked={value.includes(id)}
              disabled={id === 'kr-mfds' && !kr?.ready}
              onChange={(e) => onChange(e.target.checked ? [...value, id] : value.filter((x) => x !== id))}
            />
            {id === 'us-fda' ? 'FDA(US)' : 'MFDS(KR)'}
          </label>
        ))}
      </div>
      {!value.length && <Alert tone="amber">{t('markets.requireCountry')}</Alert>}
      {error && <Alert>{error}</Alert>}
      {kr && !kr.ready && <p className="text-xs text-amber-800">MFDS(KR): {kr.reason}</p>}
      {value.includes('kr-mfds') && (
        <div className="flex flex-wrap gap-3">
          <label>
            {t('markets.classification')}
            <Select
              value={profile.productClass}
              onChange={(e) =>
                onProfile({ ...profile, productClass: e.target.value as KrProfile['productClass'] })
              }
            >
              <option value="unknown">{t('markets.unknown')}</option>
              <option value="health_functional_food">건강기능식품</option>
              <option value="ordinary_food">{t('markets.ordinaryUnsupported')}</option>
            </Select>
          </label>
          <label>
            {t('markets.context')}
            <Select
              value={profile.context}
              onChange={(e) => onProfile({ ...profile, context: e.target.value as KrProfile['context'] })}
            >
              <option value="label">{t('markets.label')}</option>
              <option value="advertising">{t('markets.advertising')}</option>
            </Select>
          </label>
          <label>
            {t('markets.dailyServings')}
            <Input
              type="number"
              min="0.1"
              max="100"
              step="0.1"
              value={profile.servingsPerDay}
              onChange={(e) => onProfile({ ...profile, servingsPerDay: Number(e.target.value) })}
            />
          </label>
        </div>
      )}
    </fieldset>
  );
}
